import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSeo } from "../useSeo";
import { trackEvent } from "../trackEvent";
import { VENUE, PIX, CONTACT_EMAIL } from "../data";
import ChannelsList from "../ChannelsList";
import { API_BASE } from "../../api/client";

export default function Contato() {
  useSeo({
    title: "Contato | Igreja Batista Mosaico",
    description: "Fale com a Igreja Batista Mosaico em Osasco: endereço, formulário de contato e formas de contribuir via Pix.",
    path: "/contato"
  });

  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState(location.state?.prefillMessage || "");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const startedTracked = useRef(false);
  const giveCardRef = useRef(null);

  useEffect(() => {
    if (location.hash === "#give-card-section" && giveCardRef.current) {
      giveCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  function handleFocusIn() {
    if (!startedTracked.current) {
      startedTracked.current = true;
      trackEvent("form_start", { form_name: "contato" });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    trackEvent("form_submit", { form_name: "contato" });
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/contato`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: name.trim(), email: email.trim(), telefone: phone.trim(), mensagem: msg.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Não foi possível enviar sua mensagem agora.");
      }
      setStatus("sent");
      setName("");
      setEmail("");
      setPhone("");
      setMsg("");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "Não foi possível enviar sua mensagem agora. Tente novamente em instantes.");
    }
  }

  function copy(text, setFlag) {
    navigator.clipboard?.writeText(text).then(() => {
      setFlag(true);
      setTimeout(() => setFlag(false), 2000);
    });
  }

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Contato</p>
        <h1>Fale com a gente</h1>
        <p>Primeira vez, dúvida sobre um ministério, ou só quer dizer oi — a mensagem chega pra alguém da equipe.</p>
      </div>

      <div className="grid grid-2" style={{ alignItems: "start" }}>
        <div className="card venue-card">
          <img className="venue-photo venue-photo--map" src={VENUE.photoContato} alt="Mapa com a entrada da Igreja Batista Mosaico pela Av. dos Autonomistas" />
          <div className="addr-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.4" /></svg>
            <div>
              <div className="addr-text">{VENUE.address}</div>
              <a className="maplink" href={VENUE.mapsUrl} target="_blank" rel="noopener">Ver rota no mapa
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
              </a>
            </div>
          </div>
          <div className="venue-caption">Como chegar: entrada pela Av. dos Autonomistas.</div>
        </div>

        <div className="card">
          {status === "sent" && (
            <div className="confirm-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              Recebemos seu contato — alguém da equipe responde em breve.
            </div>
          )}
          {status === "error" && (
            <div className="confirm-banner confirm-banner-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
              {errorMsg}
            </div>
          )}
          <form onSubmit={handleSubmit} onFocus={handleFocusIn}>
            <div className="form-row"><label htmlFor="cname">Nome <span className="form-required">*</span></label><input id="cname" type="text" required placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} disabled={status === "sending"} /></div>
            <div className="form-row"><label htmlFor="cemail">E-mail <span className="form-required">*</span></label><input id="cemail" type="email" required placeholder="voce@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={status === "sending"} /></div>
            <div className="form-row"><label htmlFor="cphone">Telefone <span className="form-optional">(opcional)</span></label><input id="cphone" type="tel" placeholder="(11) 90000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={status === "sending"} /></div>
            <div className="form-row"><label htmlFor="cmsg">Mensagem <span className="form-required">*</span></label><textarea id="cmsg" rows="3" required placeholder="Como podemos ajudar?" value={msg} onChange={(e) => setMsg(e.target.value)} disabled={status === "sending"} /></div>
            <button className="submit-btn" type="submit" disabled={status === "sending"}>{status === "sending" ? "Enviando…" : "Enviar"}</button>
            <p className="form-note">Sua mensagem chega direto para {CONTACT_EMAIL}.</p>
          </form>
        </div>
      </div>

      <div className="card give-card" id="give-card-section" ref={giveCardRef} style={{ marginTop: 20 }}>
        <div className="give-lead">
          <p className="eyebrow" style={{ marginBottom: 6 }}>Contribuição</p>
          <h2 style={{ fontSize: 20, fontWeight: 800 }}>Pix de contribuição</h2>
          <p className="lead">Contribua se fizer sentido pra você. Sem pressa, sem pressão. Só gratidão.</p>
          <div className="give-key-row">
            <span className="give-key">{PIX.key}</span>
            <button className="give-copy-btn" type="button" onClick={() => copy(PIX.key, setCopiedKey)}>{copiedKey ? "Copiado!" : "Copiar chave"}</button>
          </div>
          <div className="give-dest">
            {PIX.destinations.map((d) => <span key={d}><b>{d.slice(0, 2)}</b> — {d.slice(5)}</span>)}
          </div>
          <p className="form-note" style={{ marginTop: 8 }}>Use os centavos finais do valor pra indicar o destino.</p>
          <details className="give-cc">
            <summary>Prefere copiar e colar no banco?</summary>
            <code>{PIX.copiaCola}</code>
            <button className="give-copy-btn secondary" type="button" style={{ marginTop: 8 }} onClick={() => copy(PIX.copiaCola, setCopiedCode)}>{copiedCode ? "Copiado!" : "Copiar código copia e cola"}</button>
          </details>
        </div>
        <div className="give-qr-wrap">
          <img src={PIX.qrImage} alt={`QR Code Pix para ${PIX.key}`} />
          <span>Aponte a câmera do banco</span>
        </div>
      </div>

      <ChannelsList title="Ou nos encontre por aqui" />
    </>
  );
}
